import { FeatureCollection } from 'geojson';
import {
  types, Instance, addDisposer,
} from 'mobx-state-tree';
import { reaction } from 'mobx';
import centerOfMass from '@turf/center-of-mass';
import { points } from '@turf/helpers';
import pointsWithinPolygon from '@turf/points-within-polygon';
import bbox from '@turf/bbox';
import { PickInfo, Position } from 'deck.gl';
import { EditAction } from '@nebula.gl/edit-modes';
import { LassoPoint } from '../../../../components/map/types';
import {
  VisitDtoModelType, MapMarkerType, RouteDtoModelType, RouteTimelineDtoModelType,
} from '../../models';
import type { StopSelectionAnchorsByRoute } from '../../../../components/routes/RoutesGantt/RoutesGanttChart/stopSelection';
import {
  CursorTooltipStatus, LassoEditHandleType, LassoGuideType, LassoMode, MapEditType,
} from '../../../../components/map/constants';
import {
  getLassoCursorTooltipKey, getLassoEditEffect, isLassoClosingPoint, isLassoEditHandle,
} from '../../../../components/map/utils/lassoInteraction';
import getAppRoot from '../../utils/getAppRoot';
import { ItemsArray } from './ItemsArray';
import { getItemizedOrders, getItemizedStops } from '../helpers/getItems';

export type ItemsArrayModelType = Instance<typeof ItemsArray>;

export const props = {
  selectedItems: types.maybe(ItemsArray),
  selectedStopAnchorsByRoute: types.optional(types.frozen<StopSelectionAnchorsByRoute>(), {}),
  targetRouteUuid: types.union(types.string, types.undefined),
  lassoPoints: types.array(types.frozen<LassoPoint>()),
  lassoMode: types.maybeNull(types.enumeration<LassoMode>('LassoMode', Object.values(LassoMode))),
  cursorTooltip: types.maybeNull(types.enumeration<CursorTooltipStatus>('CursorTooltipStatus', Object.values(CursorTooltipStatus))),
  activeEditHandle: types.maybe(types.frozen()),
  drawnPolygon: types.optional(types.frozen<FeatureCollection>(), {
    type: 'FeatureCollection',
    features: [],
  }),
};

export const views = (self: any) => ({
  get isLassoMode(): boolean {
    return !!self.lassoMode;
  },
  get isDrawingLassoMode(): boolean {
    return self.lassoMode === LassoMode.DRAWPOLYGON;
  },
  get isEditingLassoMode(): boolean {
    return self.lassoMode === LassoMode.EDITING;
  },
  get selectedUnscheduledOrdersWithinLasso(): VisitDtoModelType[] | undefined {
    return self.selectedItems?.unscheduledOrders || [];
  },
  get selectedScheduledOrdersWithinLasso(): VisitDtoModelType[] | undefined {
    return self.selectedItems?.scheduledOrders;
  },
  get selectedScheduledOrderUuidsWithinLasso(): string[] {
    return self.selectedScheduledOrdersWithinLasso.filter((visit: VisitDtoModelType) => visit?.uuid).map((visit: VisitDtoModelType) => visit?.uuid!);
  },
  get ordersWithinLasso(): VisitDtoModelType[] {
    return self.selectedItems?.allOrders;
  },
  get ordersWithinLassoCount(): number {
    return self.selectedItems?.allOrdersCount || 0;
  },
  get selectedVisitsAreScheduled(): boolean {
    return !!self.selectedItems?.stops.length;
  },
  get selectedVisitsRoutes(): RouteDtoModelType[] {
    return self.selectedItems?.stops ? [...new Set(self.selectedItems?.stops.map((v: RouteTimelineDtoModelType) => v.route))] as RouteDtoModelType[] : [];
  },
  get cursorTooltipText(): string | null {
    let status;

    switch (true) {
      case !!self.cursorTooltip:
        status = self.cursorTooltip;
        break;
      case self.isDrawingLassoMode && !self.lassoPoints?.length:
        status = CursorTooltipStatus.PREDRAW_LASSO;
        break;
      case self.isHoveringWithinLasso && !self.draggingVisit && !self.activeEditHandle:
        status = CursorTooltipStatus.REASSIGN_HELPER;
        break;
      default:
        status = null;
    }

    return getLassoCursorTooltipKey(status);
  },
  get lassoCentroid(): any | null {
    return self.drawnPolygon.features.length ? centerOfMass(self.drawnPolygon) : null;
  },
  get lassoBoundingBox(): any | null {
    return self.drawnPolygon.features.length ? bbox(self.drawnPolygon) : null;
  },
  get lassoBoundingBoxMaxY(): number | undefined {
    return self.lassoBoundingBox?.[3];
  },
  get lassoActionToolbarCoordinates(): Position | null {
    return self.lassoBoundingBoxMaxY && self.lassoCentroid ? [self.lassoCentroid.geometry.coordinates[0], self.lassoBoundingBoxMaxY] : null;
  },
  get hasSelectionToolbar(): boolean {
    return self.lassoActionToolbarCoordinates && self.lassoMode;
  },
  get isHandlingManyStopsFromManyRoutes(): boolean {
    const uniqueRoutes = self.selectedVisitsRoutes?.length;
    return self.isEditingLassoMode && self.selectedItems?.stops?.length > 1 && uniqueRoutes > 1;
  },
  get isHoveringWithinLasso(): boolean {
    const pointFeatures = self.isEditingLassoMode && self.hoveredLngLat && points([self.hoveredLngLat]);
    return pointFeatures ? !!pointsWithinPolygon(pointFeatures, self.drawnPolygon)?.features.length : false;
  },
  hoveredEditHandleType(type: LassoEditHandleType): boolean {
    return self.activeEditHandle?.properties.editHandleType === type;
  },
  isStopAssignableToRoute(handledObject: MapMarkerType, targetRoute?: RouteDtoModelType): boolean {
    const notSameRoute = targetRoute?.id !== handledObject?.properties.routeId;
    return targetRoute && handledObject && (notSameRoute || self.isHandlingManyStopsFromManyRoutes || !!self.selectedItems?.unscheduledOrders?.length);
  },
  isMarkerInLasso(marker: MapMarkerType): boolean {
    return self.isEditingLassoMode && self.selectedMarkers.find((d: MapMarkerType) => marker.properties?.id === d.properties.id);
  },
  isClosingPoint(point: any): boolean {
    return isLassoClosingPoint(point, self.lassoPoints, self.isDrawingLassoMode);
  },
  getTransformedVisitFromPolygon(polygonPicked: any) {
    if (polygonPicked.object?.properties.guideType === LassoGuideType.EDIT_HANDLE) {
      return polygonPicked;
    }
    const marker = self.selectedMarkers?.[0];

    if (marker) {
      self.setIsDraggable(false);
    } else {
      self.setIsDraggable(true);
    }

    return polygonPicked.object ? {
      ...polygonPicked,
      object: {
        ...polygonPicked.object,
        geometry: {
          ...polygonPicked.object.geometry,
          type: 'Point',
        },
        properties: marker?.properties,
      },

    } : polygonPicked;
  },
});

export const actions = (self: any) => ({
  setSelectedStopAnchorsByRoute(anchorsByRoute?: StopSelectionAnchorsByRoute) {
    self.selectedStopAnchorsByRoute = anchorsByRoute ?? {};
  },
  clearSelectedStopAnchorsByRoute() {
    self.selectedStopAnchorsByRoute = {};
  },
  setLassoMode(mode: LassoMode | null) {
    self.lassoMode = mode;
    if (mode === null) {
      self.isDraggable = true;
    }
  },
  setTargetRouteUuid(uuid: string | null) {
    self.targetRouteUuid = uuid;
  },
  drawPolygon(polygon: FeatureCollection | null) {
    self.drawnPolygon = polygon;
  },
  resetPolygon() {
    self.drawnPolygon = {
      type: 'FeatureCollection',
      features: [],
    };
  },
  setLassoPoints(drawnPoints: LassoPoint[]) {
    self.lassoPoints = drawnPoints;
  },
  setCursorTooltip(status: CursorTooltipStatus | null) {
    self.cursorTooltip = status;
  },
  setActiveEditHandle(object: any) {
    self.activeEditHandle = object;
  },
  setSelectedItems(items?: { unscheduledOrders?: string[], stops?: string[] }) {
    const numOfSelectedRoutes = getAppRoot(self).ui.selectedRoutesBulk;
    if (numOfSelectedRoutes.size > 0) {
      getAppRoot(self).ui.clearSelectedRoutesBulk();
    }
    self.selectedItems = undefined;
    if (!items?.stops?.length) {
      self.clearSelectedStopAnchorsByRoute();
    }
    if (items) {
      self.isLoadingMarkerVisits = true;
      const selectedItems: { stops: RouteTimelineDtoModelType[], unscheduledOrders: VisitDtoModelType[] } = { unscheduledOrders: [], stops: [] };

      selectedItems.stops = getItemizedStops(self, self.selectedItems, items.stops);
      selectedItems.unscheduledOrders = getItemizedOrders(self, self.selectedItems, items.unscheduledOrders);

      self.selectedItems = selectedItems ? selectedItems as ItemsArrayModelType : undefined;

      self.isLoadingMarkerVisits = false;
      getAppRoot(self).mapView.exitDrawMode();
    }
  },
  handleEditLassoSelection({ updatedData, editType, editContext }: EditAction<any>) {
    const { enterEditing, nextDraggable } = getLassoEditEffect(editType);
    if (nextDraggable !== null) {
      self.setIsDraggable(nextDraggable);
    }
    if (editType === MapEditType.ADD_TENTATIVE_POSITION) {
      self.setLassoPoints([...self.lassoPoints, editContext]);
    }
    if (enterEditing) {
      self.setLassoMode(LassoMode.EDITING);
    }
    self.drawPolygon(updatedData);
  },
  handleCompleteLassoSelection(info: any) {
    self.selectMarkers(info.selectedFeatures);
  },
  handleHoverLassoSelection(hovered: PickInfo<any>) {
    if (hovered.object) {
      self.setIsDraggable(false);
    } else {
      self.setIsDraggable(true);
    }
    if (isLassoEditHandle(hovered.object)) {
      self.setActiveEditHandle(hovered.object);
    } else {
      self.setActiveEditHandle(undefined);
    }
    if (self.isClosingPoint(hovered?.object)) {
      self.setCursorTooltip(CursorTooltipStatus.CLOSING_LASSO);
    } else {
      self.setCursorTooltip(null);
    }
  },
});

export const onChangeLassoMode = (self: any) => {
  addDisposer(self, reaction(() => self.lassoMode, (lassoMode) => {
    if (self.isDrawingLassoMode || !lassoMode) {
      self.resetPolygon();
    }
    self.setCursorTooltip(null);
    self.setLassoPoints([]);
    self.selectMarkers(undefined);
    getAppRoot(self).ui.clearSelectedRoutesBulk();
    getAppRoot(self).ui.selectVisit(undefined);
  }));

  return {};
};
